<script setup>
import {router, usePage} from "@inertiajs/vue3";
import CLink from "@/Components/CLink.vue";
import {nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";

const page = usePage()
const categoriesTextColor = portalOptions.categoriesTextColor
const tab = ref(null)
const isHovering = ref(false)
const navBarRef = ref(null)
const submenuTop = ref(0)
const viewportRef = ref(null)
const showLeftArrow = ref(false)
const showRightArrow = ref(false)
const isOverflowing = ref(false)
let recomputeTimer = null
let closeHoverTimer = null
let resizeObserver

const hasSubItems = (itemId) => {
    return page.props.portalMenuTags.some(tag => tag.type === 'group' && String(tag.menu_id) === String(itemId))
}

function cancelCloseHover() {
    if (closeHoverTimer) {
        clearTimeout(closeHoverTimer)
        closeHoverTimer = null
    }
}

function scheduleCloseHover() {
    cancelCloseHover()
    closeHoverTimer = setTimeout(() => {
        isHovering.value = false
    }, 500)
}

function computeSubmenuTop() {
    const nav = navBarRef.value
    const el = nav && nav.$el ? nav.$el : nav
    if (!el || !el.getBoundingClientRect) {
        submenuTop.value = 0
        return
    }
    const rect = el.getBoundingClientRect()
    submenuTop.value = rect.bottom + window.scrollY
}

function requestRecomputeTop() {
    if (recomputeTimer) {
        clearTimeout(recomputeTimer)
    }
    recomputeTimer = setTimeout(() => {
        computeSubmenuTop()
        recomputeTimer = null
    }, 50)
}

const handleMouseEnter = (itemId) => {
    cancelCloseHover()
    tab.value = itemId
    isHovering.value = true
    nextTick(() => {
        updateArrowsVisibility()
        centerViewportIfOverflowing()
    })
}

const handleMouseLeave = () => {
    scheduleCloseHover()
}

const onTabClick = (item) => {
    if (!hasSubItems(item.id)) {
        router.visit(route('product.list-menu', {slug: item.slug}))
    } else {
        tab.value = item.id
        isHovering.value = true
        nextTick(() => {
            updateArrowsVisibility()
            centerViewportIfOverflowing()
        })
    }
}

function updateArrowsVisibility() {
    const el = viewportRef.value
    if (!el) {
        showLeftArrow.value = false
        showRightArrow.value = false
        isOverflowing.value = false
        return
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    isOverflowing.value = el.scrollWidth > el.clientWidth
    showLeftArrow.value = el.scrollLeft > 0
    showRightArrow.value = maxScrollLeft > 0 && el.scrollLeft < maxScrollLeft - 1
}

function centerViewportIfOverflowing() {
    const el = viewportRef.value
    if (!el) {
        return
    }
    const overflowing = el.scrollWidth > el.clientWidth
    if (!overflowing) {
        return
    }
    const target = Math.max(0, Math.floor((el.scrollWidth - el.clientWidth) / 2))
    el.scrollTo({left: target, behavior: 'auto'})
    setTimeout(updateArrowsVisibility, 50)
}

function scrollByAmount(direction) {
    const el = viewportRef.value
    if (!el) {
        return
    }
    const amount = Math.max(150, Math.floor(el.clientWidth * 0.5))
    el.scrollBy({left: direction * amount, behavior: 'smooth'})
    setTimeout(updateArrowsVisibility, 200)
}

function onViewportScroll() {
    updateArrowsVisibility()
}

function setViewportRef(el, itemId) {
    if (tab.value !== itemId) {
        return
    }
    if (resizeObserver && viewportRef.value) {
        try {
            resizeObserver.unobserve(viewportRef.value)
        } catch (e) {
        }
    }
    viewportRef.value = el
    if (resizeObserver && el) {
        try {
            resizeObserver.observe(el)
        } catch (e) {
        }
    }
    nextTick(updateArrowsVisibility)
}

onMounted(() => {
    updateArrowsVisibility()
    computeSubmenuTop()
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => updateArrowsVisibility())
        if (viewportRef.value) {
            try {
                resizeObserver.observe(viewportRef.value)
            } catch (e) {
            }
        }
    } else {
        window.addEventListener('resize', updateArrowsVisibility)
    }

    window.addEventListener('resize', requestRecomputeTop, {passive: true})
    window.addEventListener('scroll', requestRecomputeTop, {passive: true})

    watch([tab, isHovering], async () => {
        await nextTick()
        computeSubmenuTop()
        updateArrowsVisibility()
        centerViewportIfOverflowing()
    })
})

onBeforeUnmount(() => {
    if (resizeObserver && viewportRef.value) {
        try {
            resizeObserver.unobserve(viewportRef.value)
        } catch (e) {
        }
    }
    window.removeEventListener('resize', updateArrowsVisibility)
})
</script>

<template>
    <v-sheet color="secondary" class="mx-auto w-100 category-nav-bar" ref="navBarRef">
        <v-tabs v-model="tab" align-tabs="center" height="85" class="tabs-full-height d-flex justify-center">
            <v-tab
                v-for="item in page.props.portalMenuItems.filter(i => i.pai === null)"
                :key="item.id"
                :value="item.id"
                class="tab-item d-flex flex-column align-center justify-center px-3"
                @mouseenter="hasSubItems(item.id) && handleMouseEnter(item.id)"
                @mouseleave="handleMouseLeave"
                @click="onTabClick(item)"
            >
                <div class="d-flex flex-column align-center rounded py-1">
                    <c-link :href="route('product.list-menu', {slug: item.slug})">
                        <v-img :src="item.icon_url" height="35px" width="40px" class="mb-2 mx-auto"/>
                        <p class="tab-title" :style="{color: categoriesTextColor}">{{ item.title }}</p>
                    </c-link>
                </div>
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <template v-for="item in page.props.portalMenuItems.filter(i => i.pai === null)" :key="item.id">
                <v-tabs-window-item :value="item.id">
                    <v-card class="submenu-card" @mouseenter="handleMouseEnter(item.id)" @mouseleave="handleMouseLeave">
                    </v-card>
                    <teleport to="body">
                        <v-sheet
                            v-if="isHovering && tab === item.id"
                            class="submenu-item-box"
                            :style="{
                                position: 'fixed',
                                left: '0',
                                right: '0',
                                top: submenuTop + 'px',
                                backgroundColor: '#454545',
                                overflowY: 'hidden',
                                overflowX: 'hidden',
                                zIndex: 1100
                            }"
                            @mouseenter="handleMouseEnter(item.id)"
                            @mouseleave="handleMouseLeave"
                        >
                            <div class="subitems-wrapper">
                                <v-btn
                                    :class="['nav-arrow left', { 'arrow-hidden': !showLeftArrow }]"
                                    variant="text"
                                    density="comfortable"
                                    icon
                                    @click="scrollByAmount(-1)"
                                    :aria-hidden="!showLeftArrow"
                                    :tabindex="showLeftArrow ? 0 : -1"
                                >
                                    <v-icon icon="mdi-chevron-left"/>
                                </v-btn>

                                <div
                                    :ref="el => setViewportRef(el, item.id)"
                                    class="subitems-viewport"
                                    :class="{ 'center-content': !isOverflowing }"
                                    @scroll.passive="onViewportScroll"
                                >
                                    <div class="subitems-container">
                                        <template
                                            v-for="subItem in page.props.portalMenuTags.filter(si => si.type === 'group' && String(si.menu_id) === String(item.id))"
                                            :key="subItem.id">
                                            <v-sheet min-width="100" class="d-flex justify-center submenu-item-space px-2">
                                                <c-link :href="route('product.list-menu', {slug: item.slug, filters: subItem.name})"
                                                        class="sub-item-link inline-subitem">
                                                    <p class="submenu-item" :style="{color: categoriesTextColor}">
                                                        {{ subItem.name.charAt(0).toUpperCase() + subItem.name.slice(1) }}</p>
                                                </c-link>
                                            </v-sheet>
                                        </template>
                                    </div>
                                </div>

                                <v-btn
                                    :class="['nav-arrow right', { 'arrow-hidden': !showRightArrow }]"
                                    variant="text"
                                    density="comfortable"
                                    icon
                                    @click="scrollByAmount(1)"
                                    :aria-hidden="!showRightArrow"
                                    :tabindex="showRightArrow ? 0 : -1"
                                >
                                    <v-icon icon="mdi-chevron-right"/>
                                </v-btn>
                            </div>
                        </v-sheet>
                    </teleport>
                </v-tabs-window-item>
            </template>
        </v-tabs-window>
    </v-sheet>
</template>

<style scoped>
.category-nav-bar {
    z-index: 1004 !important;
    position: relative !important;
}

.sub-item-link {
    margin: 15px 0 !important;
    padding: 0 !important;
}

.submenu-item {
    transition: color 0.2s ease;
    text-align: center;
}

.submenu-item:hover {
    color: rgb(var(--v-theme-primary)) !important;
}

.submenu-item-box {
    padding: 0 !important;
}

.submenu-item-space {
    background-color: transparent;
}

.submenu-item-space:hover {
    background-color: #3c3c3c;
}

@media (max-width: 1024px) {
    .category-nav-bar {
        top: 4rem !important;
        position: relative !important;
        margin-bottom: 5%;
    }

    .submenu-item-box {
        padding: 0 2% !important;
    }
}

@media (max-width: 768px) {
    .category-nav-bar {
        top: 4.2rem !important;
        position: relative !important;
        margin-bottom: 8%;
    }

    .submenu-item-box {
        padding: 0 5% !important;
    }

    .subitems-wrapper {
        --arrow-gutter: 36px;
    }
}

@media (max-width: 430px) {
    .category-nav-bar {
        top: 4.2rem !important;
        position: relative !important;
        margin-bottom: 18%;
    }

    .subitems-wrapper {
        --arrow-gutter: 28px;
    }
}

.subitems-wrapper {
    position: relative;
    display: grid;
    grid-template-columns: var(--arrow-gutter) 1fr var(--arrow-gutter);
    align-items: center;
    column-gap: 8px;
    width: 100%;
    --arrow-gutter: 44px;
}

.subitems-viewport {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    width: 100%;
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
}

.subitems-viewport.center-content {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
}

.subitems-viewport {
    scrollbar-width: none;
}

.subitems-viewport::-webkit-scrollbar {
    display: none;
}

.subitems-container {
    display: flex;
    width: max-content;
    flex: 0 0 auto;

    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px 5px;
    padding: 0 !important;
    margin: 0 auto;
}

.inline-subitem {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    white-space: nowrap;
}

.nav-arrow {
    position: static;
    transform: none;
    color: #fff;
    background: rgba(0, 0, 0, 0.25);
    border-radius: 999px;
    backdrop-filter: blur(2px);
    width: var(--arrow-gutter);
    min-width: var(--arrow-gutter);
    justify-self: center;
}

.nav-arrow.left {
}

.nav-arrow.right {
}

.arrow-hidden {
    visibility: hidden;
    pointer-events: none;
}

.tab-item {
    min-height: 72px;
}

.tab-item .v-img {
    display: block;
}

.tab-title {
    font-size: 12px;
    line-height: 1.1;
    text-align: center;
    margin: 0;
    display: block;
}

.tabs-full-height {
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>